import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { observable } from "mobx";

import { PermissionBit } from "@/api";

import { MedicationDto } from "../../../api/api-connector";
import { AccessRules } from "../../../constants/roles";
import { DataModel } from "../../../models/data-model";
import { dayNames } from "../../../utils/data-utils";
import { getServerTodayISO } from "../../../utils/server-time";
import { frequencyUnitOf } from "../frequency-unit";
import { everyNth } from "../schedule-utils";

export class Medications extends DataModel<MedicationDto> {
  @observable accessor switched = false;
  switchedOrder = [1, 2, 0];
  origOrder = [2, 0, 1];

  @observable accessor changeId = null;
  @observable accessor changeDosageField: any = {
    medication_dosage: [],
    medication_change_date: getServerTodayISO(),
  };

  constructor(
    data: MedicationDto[],
    permissions: PermissionBit[],
    userUUID: string,
  ) {
    super(data, permissions, userUUID);
    this.hasHistory = true;

    // access
    this.access = {
      editRules: {
        medical: AccessRules.owner,
        monitoring: AccessRules.visible,
      },
      deleteRules: {
        medical: AccessRules.none,
        monitoring: AccessRules.visible,
      },
      historyRequiresMonitoring: true,
    };
  }

  freqDays(member: MedicationDto) {
    return (member.medication_frequency_on_days ?? [])
      .map((day) => i18n._(dayNames[day]))
      .join("/");
  }

  /** Whether this medication repeats by interval rather than on named days. */
  repeatsByInterval(member: MedicationDto) {
    return (member.medication_frequency_on_days ?? []).length === 0;
  }

  freqString(member: MedicationDto) {
    // The day list decides, not the interval: the API defaults
    // `repeat every` to 1 on every medication row, so a day-scheduled one
    // carries an inert interval too (Seerlinq/db-api#744) — and reading that
    // first would print "Every 1st day" for a Mon/Wed/Fri schedule.
    const repeatEvery = member.medication_frequency_regular_repeat_every;
    if (this.repeatsByInterval(member) && repeatEvery != null) {
      return everyNth(repeatEvery, frequencyUnitOf(member));
    } else {
      return i18n._(msg`Days: ${this.freqDays(member)}`);
    }
  }

  startChangingDosage(item: MedicationDto) {
    // The set the inline editors are gated on — everything the change-dosage
    // endpoint accepts. Dose and unit belong here too: adapting a dose is as
    // often a change of strength ("5 mg" -> "10 mg") as of how many are taken.
    this.startEditingItem(item, [
      "medication_dose",
      "medication_unit",
      "medication_dosage",
      "medication_frequency_regular_repeat_every",
    ]);

    this.editingDraft["medication_change_date"] = getServerTodayISO();
  }

  isEditingItem(item: MedicationDto) {
    const idAttr = this.idAttr;
    return (
      this.editingItemId !== undefined &&
      idAttr != null &&
      this.editingItemId === item[idAttr]
    );
  }

  stopChangingDosage() {
    this.changeId = null;
    this.changeDosageField = {
      medication_dosage: [],
      medication_change_date: getServerTodayISO(),
    };
  }
}
